import { useState } from 'react';
import { useAdminExpenses, useCreateExpense, useUpdateExpense, useDeleteExpense } from '@/hooks/useAdmin';
import { BusinessExpense, BusinessExpenseCreate } from '@/services/admin';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import {
  Wallet,
  Plus,
  Edit2,
  Trash2,
  Server,
  Globe,
  Cloud,
  Megaphone,
  MoreHorizontal,
  Calendar,
  RefreshCw,
} from 'lucide-react';

const categoryIcons: Record<string, React.ReactNode> = {
  hosting: <Server className="h-5 w-5" />,
  api_services: <Cloud className="h-5 w-5" />,
  domain: <Globe className="h-5 w-5" />,
  marketing: <Megaphone className="h-5 w-5" />,
  other: <MoreHorizontal className="h-5 w-5" />,
};

const categoryLabels: Record<string, string> = {
  hosting: 'Hosting',
  api_services: 'APIs',
  domain: 'Dominio',
  marketing: 'Marketing',
  other: 'Otros',
};

const categoryColors: Record<string, string> = {
  hosting: 'bg-blue-100 text-blue-600',
  api_services: 'bg-purple-100 text-purple-600',
  domain: 'bg-green-100 text-green-600',
  marketing: 'bg-amber-100 text-amber-600',
  other: 'bg-gray-100 text-gray-600',
};

export default function AdminExpenses() {
  const { data: expenses, isLoading } = useAdminExpenses();
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const deleteExpense = useDeleteExpense();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<BusinessExpense | null>(null);
  const [formData, setFormData] = useState<{
    category: string;
    description: string;
    amount: number;
    currency: string;
    expense_date: string;
    is_recurring: boolean;
    recurring_period: string | null;
    notes: string;
  }>({
    category: 'other',
    description: '',
    amount: 0,
    currency: 'USD',
    expense_date: new Date().toISOString().split('T')[0],
    is_recurring: false,
    recurring_period: null,
    notes: '',
  });

  const handleOpenCreate = () => {
    setEditingExpense(null);
    setFormData({
      category: 'other',
      description: '',
      amount: 0,
      currency: 'USD',
      expense_date: new Date().toISOString().split('T')[0],
      is_recurring: false,
      recurring_period: null,
      notes: '',
    });
    setShowModal(true);
  };

  const handleOpenEdit = (expense: BusinessExpense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      description: expense.description,
      amount: expense.amount,
      currency: expense.currency,
      expense_date: expense.expense_date.split('T')[0],
      is_recurring: expense.is_recurring,
      recurring_period: expense.recurring_period,
      notes: expense.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      ...formData,
      expense_date: new Date(formData.expense_date).toISOString(),
      recurring_period: formData.is_recurring ? formData.recurring_period : null,
    };

    if (editingExpense) {
      await updateExpense.mutateAsync({
        id: editingExpense.id,
        data,
      });
    } else {
      await createExpense.mutateAsync(data as BusinessExpenseCreate);
    }

    setShowModal(false);
    setEditingExpense(null);
  };

  const handleDelete = async () => {
    if (!editingExpense) return;
    await deleteExpense.mutateAsync(editingExpense.id);
    setShowDeleteModal(false);
    setEditingExpense(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate totals by category
  const totalsByCategory = expenses?.reduce((acc, exp) => {
    const amount = Number(exp.amount) || 0;
    acc[exp.category] = (acc[exp.category] || 0) + amount;
    return acc;
  }, {} as Record<string, number>) || {};

  const totalExpenses = expenses?.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gastos del Negocio</h1>
          <p className="text-gray-500">Registra y gestiona los gastos operativos</p>
        </div>
        <Button onClick={handleOpenCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Gasto
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="col-span-2 md:col-span-1 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <Wallet className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCurrency(totalExpenses, 'USD')}</p>
              <p className="text-xs text-slate-400">Total Gastos</p>
            </div>
          </div>
        </div>

        {Object.entries(categoryLabels).map(([key, label]) => (
          <div key={key} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryColors[key]}`}>
                {categoryIcons[key]}
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(totalsByCategory[key] || 0, 'USD')}
                </p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Historial de Gastos</h2>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : expenses && expenses.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {expenses.map((expense) => (
              <div
                key={expense.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryColors[expense.category]}`}>
                    {categoryIcons[expense.category]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">{expense.description}</h3>
                      {expense.is_recurring && (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-full">
                          <RefreshCw className="h-3 w-3" />
                          {expense.recurring_period === 'monthly' ? 'Mensual' : 'Anual'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                      <span className="capitalize">{categoryLabels[expense.category]}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(expense.expense_date)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">
                      {formatCurrency(expense.amount, expense.currency)}
                    </p>
                    <p className="text-xs text-gray-500">{expense.currency}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEdit(expense)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingExpense(expense);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wallet className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No hay gastos registrados</p>
            <p className="text-sm text-gray-400 mt-1">Registra tu primer gasto para comenzar</p>
            <Button onClick={handleOpenCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Gasto
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingExpense(null);
        }}
        title={editingExpense ? 'Editar Gasto' : 'Nuevo Gasto'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="hosting">Hosting</option>
              <option value="api_services">APIs / Servicios</option>
              <option value="domain">Dominio</option>
              <option value="marketing">Marketing</option>
              <option value="other">Otros</option>
            </select>
          </div>

          <Input
            label="Descripción"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Ej: Servidor VPS Hetzner"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Monto"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
              min={0}
              step={0.01}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="USD">USD</option>
                <option value="ARS">ARS</option>
              </select>
            </div>
          </div>

          <Input
            label="Fecha"
            type="date"
            value={formData.expense_date}
            onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
            required
          />

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_recurring"
              checked={formData.is_recurring}
              onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
              className="h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
            />
            <label htmlFor="is_recurring" className="text-sm text-gray-700">
              Gasto recurrente
            </label>
          </div>

          {formData.is_recurring && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
              <select
                value={formData.recurring_period || 'monthly'}
                onChange={(e) => setFormData({ ...formData, recurring_period: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="monthly">Mensual</option>
                <option value="yearly">Anual</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Notas adicionales..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditingExpense(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={createExpense.isPending || updateExpense.isPending}
            >
              {editingExpense ? 'Guardar Cambios' : 'Crear Gasto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setEditingExpense(null);
        }}
        title="Eliminar Gasto"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            ¿Estás seguro de que deseas eliminar el gasto{' '}
            <strong>{editingExpense?.description}</strong>?
          </p>
          <p className="text-sm text-gray-500">Esta acción no se puede deshacer.</p>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setEditingExpense(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              loading={deleteExpense.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
